import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';

export interface PlacementFormValues {
  placementArea: string;
  placementAisle: string;
  placementSection: string;
  placementShelf: string;
  placementPosition: string;
  shelfCapacityUnits: number | null;
  shelfFacings: number | null;
}

export function shelfCapacityUnitsValidator(control: AbstractControl): ValidationErrors | null {
  const val = control.value;
  if (val === null || val === undefined || val === '') {
    return null;
  }
  const num = Number(val);
  if (!Number.isFinite(num) || num < 0) {
    return { nonNegative: { message: 'Must be 0 or greater' } };
  }
  return null;
}

export function shelfFacingsValidator(control: AbstractControl): ValidationErrors | null {
  const val = control.value;
  if (val === null || val === undefined || val === '') {
    return null;
  }
  const num = Number(val);
  if (!Number.isInteger(num) || num < 0) {
    return { integerMin: { message: 'Must be a whole number of 0 or more' } };
  }
  return null;
}

export function createPlacementFormControls(): Record<string, FormControl> {
  return {
    placementArea: new FormControl(''),
    placementAisle: new FormControl(''),
    placementSection: new FormControl(''),
    placementShelf: new FormControl(''),
    placementPosition: new FormControl(''),
    shelfCapacityUnits: new FormControl(null, [shelfCapacityUnitsValidator]),
    shelfFacings: new FormControl(null, [shelfFacingsValidator])
  };
}

export function patchPlacement(form: FormGroup, placement: any): void {
  if (!placement) {
    return;
  }
  form.patchValue({
    placementArea: placement.area || '',
    placementAisle: placement.aisle || '',
    placementSection: placement.section || '',
    placementShelf: placement.shelf || '',
    placementPosition: placement.position || '',
    shelfCapacityUnits: placement.shelfCapacityUnits ?? null,
    shelfFacings: placement.shelfFacings ?? null
  });
}

export function buildPlacementPayload(formValue: any): Record<string, any> {
  return {
    area: (formValue.placementArea || '').trim(),
    aisle: (formValue.placementAisle || '').trim(),
    section: (formValue.placementSection || '').trim(),
    shelf: (formValue.placementShelf || '').trim(),
    position: (formValue.placementPosition || '').trim(),
    shelfCapacityUnits: normalizeNumericField(formValue.shelfCapacityUnits),
    shelfFacings: normalizeNumericField(formValue.shelfFacings)
  };
}

function normalizeNumericField(val: any): number | null {
  if (val === null || val === undefined || val === '') {
    return null;
  }
  const num = Number(val);
  return Number.isFinite(num) ? num : null;
}

export function snapshotPlacementValues(formValue: any): PlacementFormValues {
  return {
    placementArea: (formValue.placementArea || '').trim(),
    placementAisle: (formValue.placementAisle || '').trim(),
    placementSection: (formValue.placementSection || '').trim(),
    placementShelf: (formValue.placementShelf || '').trim(),
    placementPosition: (formValue.placementPosition || '').trim(),
    shelfCapacityUnits: normalizeNumericField(formValue.shelfCapacityUnits),
    shelfFacings: normalizeNumericField(formValue.shelfFacings)
  };
}

export function hasPlacementInput(values: PlacementFormValues): boolean {
  return !!(
    values.placementArea ||
    values.placementAisle ||
    values.placementSection ||
    values.placementShelf ||
    values.placementPosition ||
    values.shelfCapacityUnits !== null ||
    values.shelfFacings !== null
  );
}

export function placementValuesEqual(a: PlacementFormValues, b: PlacementFormValues): boolean {
  return (
    a.placementArea === b.placementArea &&
    a.placementAisle === b.placementAisle &&
    a.placementSection === b.placementSection &&
    a.placementShelf === b.placementShelf &&
    a.placementPosition === b.placementPosition &&
    a.shelfCapacityUnits === b.shelfCapacityUnits &&
    a.shelfFacings === b.shelfFacings
  );
}
